import { FC } from "react";
import { IonButton, IonCard, IonCardContent, IonSpinner } from "@ionic/react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { AssignmentPlatform } from "refsix-core";
import { canImportInApp } from "./canImportInApp";
import { LastAssignmentImport } from "./useAssignmentImport";
import "./assignmentPlatformCard.css";

const STEPS = ["inAppStepOpen", "inAppStepSignIn", "inAppStepDone"];

/**
 * A scraped platform's page, in one card with the import button under it
 * (REFSIX-568) — the same shape as the Ref Insight page.
 *
 * There is one import path, an in-app WebView on the referee's own phone, so
 * the only thing this decides is whether this device can run it. Where it
 * cannot — a desktop browser, mobile web — there is no button at all and the
 * card says where the import lives instead, rather than a dead "tap Import"
 * step and a button that opens nothing.
 *
 * Neither scraped platform has agreed to anything, so the disclaimer is part of
 * the card and sits above the button: the referee reads it before they start.
 */
export const AssignmentPlatformCard: FC<{
    platform: AssignmentPlatform;
    importing: boolean;
    lastImport?: LastAssignmentImport;
    onImport: () => void;
}> = ({ platform, importing, lastImport, onImport }) => {
    const { t } = useTranslation();
    const name = platform.displayName;
    const canImport = canImportInApp(platform);

    return (
        <>
            <IonCard className="assignment-platform-card">
                <IonCardContent>
                    <h2>{t("integrations.assignments.page.heading")}</h2>
                    <p data-testid="assignment-platform-disclaimer">
                        <em>
                            {t("integrations.assignments.page.notAffiliated", {
                                platform: name,
                            })}
                        </em>
                    </p>
                    {/* "...right here on your phone" — true only where the
                        import can run; the needs-phone branch below says the
                        same thing for everywhere else. */}
                    {canImport && (
                        <p>
                            {t("integrations.assignments.page.inAppOverview", {
                                platform: name,
                            })}
                        </p>
                    )}

                    {/* Only for a platform with a sign-in route that cannot
                        complete in a WebView — far better said here than
                        discovered at a dead end inside the WebView. */}
                    {platform.unsupportedSignIn && (
                        <p data-testid="assignment-platform-signin-note">
                            <strong>
                                {t(
                                    "integrations.assignments.page.signInUnsupported",
                                    {
                                        provider: platform.unsupportedSignIn,
                                        platform: name,
                                    }
                                )}
                            </strong>
                        </p>
                    )}

                    <ul>
                        {platform.importsOfficials && (
                            <li>
                                {t(
                                    "integrations.assignments.page.importsOfficials"
                                )}
                            </li>
                        )}
                        {platform.importsFees && (
                            <li>
                                {t("integrations.assignments.page.importsFees")}
                            </li>
                        )}
                    </ul>

                    {canImport ? (
                        <>
                            <h3>
                                {t("integrations.assignments.page.inAppTitle")}
                            </h3>
                            <p>
                                {t(
                                    "integrations.assignments.page.inAppDescription",
                                    { platform: name }
                                )}
                            </p>
                            <h3 data-testid="assignment-platform-steps">
                                {t("integrations.assignments.page.stepsTitle")}
                            </h3>
                            <ol>
                                {STEPS.map((step) => (
                                    <li key={step}>
                                        {t(
                                            `integrations.assignments.page.${step}`,
                                            { platform: name }
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </>
                    ) : (
                        <>
                            <h3 data-testid="assignment-platform-needs-phone">
                                {t("integrations.assignments.page.needsPhone")}
                            </h3>
                            <p>
                                {t(
                                    "integrations.assignments.page.needsPhoneDescription",
                                    { platform: name }
                                )}
                            </p>
                        </>
                    )}

                    <p
                        className="assignment-platform-status"
                        data-testid="assignment-platform-last-import"
                    >
                        {lastImport
                            ? t("integrations.assignments.page.lastImport", {
                                  count:
                                      (lastImport.added || 0) +
                                      (lastImport.updated || 0),
                                  when: moment(lastImport.at).fromNow(),
                              })
                            : t("integrations.assignments.page.neverImported", {
                                  platform: name,
                              })}
                    </p>
                </IonCardContent>
            </IonCard>

            {canImport && (
                <IonCard>
                    <IonButton
                        expand="block"
                        disabled={importing}
                        data-testid="assignment-platform-import-in-app"
                        onClick={onImport}
                    >
                        {importing && (
                            <IonSpinner slot="start" name="crescent" />
                        )}
                        {importing
                            ? t("integrations.assignments.page.inAppImporting")
                            : t("integrations.assignments.page.inAppButton", {
                                  platform: name,
                              })}
                    </IonButton>
                </IonCard>
            )}
        </>
    );
};
